import React, { useEffect, useState } from "react";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { AudioProvider, useAudio } from "../Contexts/AudiosContext";
import AppRoutes from "./AppRoutes";
import Loading from "../Components/Loading";
import { LocaleProvider } from "../Contexts/LocaleContext";
import AudioPlayer from "../Components/AudioPlayer";
import { CookiesProvider } from "react-cookie";
import 'react-toastify/dist/ReactToastify.css';
import { VideoProvider } from "../Contexts/VideosContext";
import VideoDialog from "../Pages/VideoDialog";
import VideoDialogWrapper from "../Contexts/VideoDialogWrapper";
import InternetPage from "../Pages/NoInternetConnection";
import PageSeo from "./PageSeo";
import BooksService from "../Services/Books";
import GeetGovindServices from "../Services/GeetGovind";
import KalyansServices from "../Services/Kalyan";
import KalpataruServices from "../Services/Kalpataru";
import VivekService from "../Services/Vivekvani";
import ArticlesService from "../Services/Articles";
import AudioPlayerService from "../Services/AudioPlayer";
import { Type } from "react-toastify/dist/utils";
import LoginServices from "../Services/Login";
import UTMService from "../Services/UTMService";
import { buildUTMPayload, hasUTMParams, stripUTMFromSearch } from "../utils/utmUtils";

// Browser-safe normalize helper used in auth checks
const normalize = (v?: string | null): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v);
  return (!s || s.trim() === "" || s === "null" || s === "undefined") ? null : s;
};

/////////////////////
// UTMHandler component
/////////////////////
function UTMHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasUTMParams(location.search)) return;

    const cleanSearch = stripUTMFromSearch(location.search);
    const cleanPath = location.pathname + cleanSearch;

    if (sessionStorage.getItem("utm_tracked")) {
      if (location.pathname + location.search !== cleanPath) {
        navigate(cleanPath, { replace: true });
      }
      return;
    }

    const payload = buildUTMPayload(location.search);
    sessionStorage.setItem("utm_tracked", "true");

    UTMService.track(payload).catch(() => {
      // Tracking must never break UX
    });

    navigate(cleanPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

/////////////////////
// DebugLocation component
/////////////////////
function DebugLocation({ onReady }: { onReady: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  const normalize = (v: string) =>
    (!v || v.trim() === "" || v === "null" || v === "undefined") ? null : v;

  const isMongoId = (str: string) => /^[0-9a-fA-F]{24}$/.test(str);
  const parts = location.pathname.split("/").filter(Boolean);
  let type = parts[0] || "";
  let whatis = parts[1] || "";


  if (type === "magazines" && parts.length >= 3) {
      type = parts[1];      
      whatis = parts[2];    
    }
  if (type === "DailySatsang" && parts.length > 2) {
    whatis = parts.slice(1).join("/");
  }

  console.log("DebugLocation:", { pathname: location.pathname, type, whatis });

  useEffect(() => {
    let cancelled = false;

    // If navigation already has state (i.e., user navigated inside app),
    // skip rebuild and mark ready immediately.
    if ((location.state && Object.keys(location.state).length > 0) || !type) {
      setLoading(false);
      onReady();
      return;
    }

    // valid routes that require a fetch to rebuild state
    const validRoutes = [
      "books", "geetgovind", "kalyan", "kalyanakalpataru",
      "vivekvani", "articles", "audios", "pravachans", "DailySatsang", "videos"
    ];

    // If route is not one of these, nothing to do
    if (!validRoutes.includes(type) || !whatis || whatis === "undefined" || whatis === "null") {
      setLoading(false);
      onReady();
      return;
    }

    const run = async () => {
      try {



        // AUDIO / PRAVACHAN: special quick path
        if (type === "audios" || type === "pravachans" || type === "DailySatsang") {
          const identifier = isMongoId(whatis) ? whatis : `slug-${whatis}`;

          console.log("App.tsx -> Fetching Audio via DebugLocation:", { type, whatis, identifier });
          const response: any = await AudioPlayerService.getAudioById(identifier, type);
          console.log("App.tsx -> Audio Response:", response);

          if (cancelled) return;
          if (response?.status && response?.result) {
            const canonicalPath = `/${type}/${response.result.slug}`;
            console.log("App.tsx -> Audio Found, navigating:", { canonicalPath, id: response.result.id });

            if (window.location.pathname !== canonicalPath || !location.state) {
              navigate(canonicalPath, {
                state: {
                  audioId: response.result.id,
                  audioslug: response.result.slug,
                  sorting: 0,
                  pageNum: 1,
                  index: 0,
                  audiocat: '',
                  audioAuthor: '',
                },
                replace: true,
              });
            }
          } else {
            console.warn("App.tsx -> Audio Fetch Failed or Not Found. Redirecting to Listing.", { type, response });
            navigate(`/${type}`, { replace: true });
          }

          setLoading(false);
          onReady();
          return;
        }

        // OTHER content types (books, geetgovind, kalyan, etc.)
        if (type === "books") {
          const identifier = isMongoId(whatis) ? whatis : `slug-${whatis}`;
          const response: any = await BooksService.getBookDetail(identifier);
          if (cancelled) return;
          if (response?.result) {
            navigate(`/books/${response.result.slug}`, {
              state: {
                bookId: response.result.id,
                bookName: response.result.name,
                bookSlug: response.result.slug,
                // Pass the full detail so BookDetailPage skips a redundant fetch
                bookDetail: response.result,
              },
              replace: true,
            });
          } else {
            navigate('/books', { replace: true });
          }
          setLoading(false);
          onReady();
          return;
        }

        if (type === "geetgovind") {
          const identifier = isMongoId(whatis) ? whatis : `slug-${whatis}`;
          const response: any = await GeetGovindServices.getcurrentGeetGovind(identifier);
          if (cancelled) return;
          if (response?.result) {
            navigate(`/magazines/geetgovind/${response.result.slug}`, {
              state: {
                MonthId: response.result.id,
                bookName: response.result.name,
                slug: response.result.slug,
              },
              replace: true,
            });
          } else {
            navigate('/magazines/geetgovind', { replace: true });
          }
          setLoading(false);
          onReady();
          return;
        }

        if (type === "kalyan") {
          const identifier = isMongoId(whatis) ? whatis : `slug-${whatis}`;
          const response: any = await KalyansServices.getcurrentKalyan(identifier);
          if (cancelled) return;
          if (response?.result) {
            navigate(`/magazines/kalyan/${response.result.slug}`, {
              state: {
                kalyanId: response.result.id,
                kalyanSlug: response.result.slug,
                bookName: response.result.name,
                // Pass full detail so KalyanDetailPage can reuse prefetched data
                kalyanDetail: response.result,
              },
              replace: true,
            });
          } else {
            navigate('/magazines/kalyan', { replace: true });
          }
          setLoading(false);
          onReady();
          return;
        }

        if (type === "kalyanakalpataru") {
          const identifier = isMongoId(whatis) ? whatis : `slug-${whatis}`;
          const response: any = await KalpataruServices.getcurrentKalpatarul(identifier);
          if (cancelled) return;
          if (response?.result) {
            navigate(`/magazines/kalyanakalpataru/${response.result.slug}`, {
              state: {
                kalpatruId: response.result.id,
                kalpatruSlug: response.result.slug,
                bookName: response.result.name,
                // Pass full detail to allow detail page to reuse prefetched data
                kalpatruDetail: response.result,
              },
              replace: true,
            });
          } else {
            navigate('/magazines/kalyanakalpataru', { replace: true });
          }
          setLoading(false);
          onReady();
          return;
        }

        if (type === "vivekvani") {
          const identifier = isMongoId(whatis) ? whatis : `slug-${whatis}`;
          const response: any = await VivekService.getVivekVaniDetail(identifier);
          if (cancelled) return;
          if (response?.result) {
            navigate(`/magazines/vivekvani/${response.result.slug}`, {
              state: {
                vivekvaniId: response.result.id,
                vivekName: response.result.name,
                vivekSlug: response.result.slug,
                // Pass full detail so VivekvaniDetailPage can reuse prefetched data
                vivekvaniDetail: response.result,
              },
              replace: true,
            });
          } else {
            navigate('/magazines/vivekvani', { replace: true });
          }
          setLoading(false);
          onReady();
          return;
        }

        if (type === "articles") {
          const identifier = isMongoId(whatis) ? whatis : `slug-${whatis}`;
          const response: any = await ArticlesService.getArticlesDetail(identifier);
          if (cancelled) return;
          if (response?.result) {
            navigate(`/articles/${response.result.slug}`, {
              state: {
                articleId: response.result.id,
                articleName: response.result.name,
                articleSlug: response.result.slug,
              },
              replace: true,
            });
          } else {
            navigate('/articles', { replace: true });
          }
          setLoading(false);
          onReady();
          return;
        }

        if (type === "videos") {
          console.log("Navigating to Section :" + type + "With videoId:", whatis);
          navigate("/videos", {
            state: {
              //  from: "apppage",
              videoId: whatis,
            },
          });
        }

        // If we reach here, nothing matched — mark ready
        setLoading(false);
        onReady();

      } catch (error) {
        console.error("DebugLocation fetch error:", error);
        if (!cancelled) {
          setLoading(false);
          // fallback: mark ready so app doesn't hang
          onReady();
        }
      }
    }; // end run

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // re-run when pathname changes

  // When still processing, show Loading so App can hold off rendering the rest
  if (loading) return <Loading />;

  return null;
}

/////////////////////
// Main App
/////////////////////
function App(props: any) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // debugReady ensures DebugLocation finished its job (rebuild state / navigate)
  const [debugReady, setDebugReady] = useState(false);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const userId = normalize(localStorage.getItem("UserId") ?? "");
      const token = normalize(localStorage.getItem("Token") ?? "");

      console.log("=== App Start: Auth Check ===");
      if (userId && token && userId !== null && userId !== undefined && userId !== "") {
        try {
          console.log(`-> Refreshing token for user: ${userId}`);
          // Passing current token as fcmToken as found in legacy code pattern
          const response: any = await LoginServices.refreshTokenV2(userId, token);
          console.log("<- Refresh Response:", response);

          if (response && (response.status === true || response.status === 200) && response.result) {
            console.log("-> Token refreshed successfully.");
            localStorage.setItem("Token", response.result.token);
            // Ensure we update other keys if returned, or keep existing
            if (response.result.userId) localStorage.setItem("UserId", response.result.userId);
            if (response.result.signKey) localStorage.setItem("SignKey", response.result.signKey);

            // Update Image from response directly (no need for extra profile call)
            if (response.result.imagePath) {
              console.log("-> Updating 'Image' from refresh response.");
              localStorage.setItem("Image", response.result.imagePath);
            }

          } else {
            console.warn("-> Refresh failed or invalid response. Clearing auth.");
            clearAuthData();
          }
        } catch (e) {
          console.error("-> Error during token refresh:", e);
          clearAuthData();
        }
      } else {
        console.log("-> No user/token found in local storage. Proceeding as guest.");
      }
      setAuthChecked(true);
      console.log("=== App Start: Auth Check Complete ===");
    };

    const clearAuthData = () => {
      console.log("-> Clearing localStorage auth data.");
      const itemsToRemove = [
        "UserId", "Token", "SignKey", "UserName",
        "Image", "Phone", "Email", "fixedUserId", "signInMode"
      ];
      itemsToRemove.forEach(item => localStorage.removeItem(item));
    };

    checkAuthStatus();
  }, []);

  // onReady callback passed to DebugLocation
  const handleDebugReady = () => setDebugReady(true);

  return (
    isOnline ? (
      <LocaleProvider>
        <CookiesProvider>
          <React.Suspense fallback={<Loading />}>
            <BrowserRouter>
              <UTMHandler />
              {!authChecked ? (
                <Loading />
              ) : (
                <>
                  {/* DebugLocation runs first (inside router) and blocks while it rebuilds state */}
                  <DebugLocation onReady={handleDebugReady} />

                  {/* Initialize providers and player only after debugReady === true */}
                  {debugReady ? (
                    <>
                      <PageSeo />
                      <VideoProvider>
                        <AudioProvider>
                          <AppRoutes />
                          <AudioPlayer />
                          <VideoDialogWrapper />
                        </AudioProvider>
                      </VideoProvider>
                    </>
                  ) : null}
                </>
              )}
            </BrowserRouter>
          </React.Suspense>
        </CookiesProvider>
      </LocaleProvider>
    ) : (
      <InternetPage />
    )
  );
}

export default App;
